/**
 * DataTable — polished, type-aware rendering of a tabular response.
 *
 * Design notes:
 * - Soft outer border + radius so the table reads as a contained surface
 *   inside the prose box (matches the agent-turn rail aesthetic).
 * - Header row is quiet: subtle bg tint, uppercase small caps. No gradient.
 * - No vertical cell borders — alternate row stripes provide rhythm.
 * - Numeric columns auto-detected by sampling values; right-aligned and
 *   set in tabular-nums so digits stack column-wise for scanning.
 * - Variance / change / delta columns render with brand-green for
 *   positives and red for negatives. Sign is preserved in the value.
 * - Header bar at the top carries the caption and the row count; the
 *   wrap supports horizontal scroll on narrow screens.
 *
 * Stylistically separate from the markdown table renderer in App.css so
 * each path can evolve independently — DataTable is used when the user
 * toggles chart → table; markdown table covers responses where no chart
 * was auto-picked at all.
 */
import React, { useMemo } from "react";
import { mergeStyleSets } from "@fluentui/react";
import type { ChartData } from "../../types/agent.types";

interface Props {
  data: ChartData;
  caption?: string;
  /** When provided, values in the primary dimension column become clickable
   *  drilldown targets. Receives the human-readable column label and the
   *  raw cell value. */
  onDrilldown?: (field: string, value: string) => void;
}

const styles = mergeStyleSets({
  wrap: {
    marginTop: 8,
    background: "#FFFFFF",
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    overflow: "hidden",
    boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
  },
  head: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 14px",
    background: "#fbfbfb",
    borderBottom: "1px solid #E5E7EB",
  },
  caption: {
    fontSize: 11,
    fontWeight: 600,
    color: "#747474",
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
  },
  rowCount: {
    fontSize: 10.5,
    color: "#9CA3AF",
    fontFamily: "'Roboto Mono', ui-monospace, Menlo, monospace",
    marginLeft: 4,
  },
  scroll: {
    overflowX: "auto" as const,
    overflowY: "auto" as const,
    maxWidth: "100%",
    // Cap height so a 50-row fallback table doesn't push the rest of the
    // conversation way down the page. ~10 rows visible, then scroll.
    maxHeight: 420,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
    fontSize: 13,
    fontFamily: "inherit",
  },
  th: {
    textAlign: "left" as const,
    background: "#fbfbfb",
    padding: "9px 14px",
    fontSize: 10.5,
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: "0.04em",
    color: "#484848",
    borderBottom: "1px solid #E5E7EB",
    whiteSpace: "nowrap" as const,
    // Sticky header keeps the column labels in view when the scroll
    // wrapper hits its maxHeight and the body scrolls.
    position: "sticky" as const,
    top: 0,
    zIndex: 1,
  } as any,
  thNum: {
    textAlign: "right" as const,
  },
  td: {
    padding: "9px 14px",
    borderBottom: "1px solid #F0F1F2",
    color: "#232323",
    fontVariantNumeric: "tabular-nums",
    verticalAlign: "top" as const,
  } as any,
  tdNum: {
    textAlign: "right" as const,
    fontWeight: 500,
  } as any,
  rowEven: {
    selectors: {
      "& td": { background: "#fcfcfc" },
    },
  } as any,
  rowHover: {
    selectors: {
      "&:hover td": { background: "#f7faf7" },
    },
  } as any,
  rowLast: {
    selectors: {
      "& td": { borderBottom: 0 },
    },
  } as any,
  deltaUp: {
    color: "#2e873d",
    fontWeight: 500,
  },
  deltaDown: {
    color: "#B42318",
    fontWeight: 500,
  },
  emptyState: {
    padding: "24px 14px",
    textAlign: "center" as const,
    fontSize: 12.5,
    color: "#9CA3AF",
  } as any,
  // Drilldown affordance — only the primary dimension column. The value gets
  // a green dashed underline + a ↳ arrow on hover so it reads as interactive
  // without shouting at rest. Reuses the brand-green tint from follow-up chips.
  drillCell: {
    cursor: "pointer",
    selectors: {
      "&:hover": { background: "#f3fcf3" },
      "&:hover .dt-drill-label": { borderBottomColor: "#2e873d", color: "#2e873d" },
      "&:hover .dt-drill-arrow": { opacity: 1, color: "#2e873d" },
      "&:focus-visible": { outline: "2px solid #2e873d", outlineOffset: "-2px" },
    },
  } as any,
  drillLabel: {
    borderBottom: "1px dashed transparent",
    transition: "border-color 0.12s, color 0.12s",
  },
  drillArrow: {
    color: "#c4c9c4",
    fontSize: 12,
    marginLeft: 6,
    opacity: 0,
    transition: "opacity 0.12s, color 0.12s",
  },
  drillHint: {
    marginLeft: "auto",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 10.5,
    fontWeight: 500,
    color: "#9CA3AF",
  } as any,
  drillHintDot: {
    width: 5,
    height: 5,
    borderRadius: "50%",
    background: "#2e873d",
  },
});

/** Identifier-shaped column NAMES disqualify a column from being treated
 *  as numeric even when its values are numbers (CostCentreNumber etc). */
const IDENTIFIER_NAME_RE = /(?:number|code|num|key|id|year|type)$/i;

/** Decide whether a column should render right-aligned + numeric by
 *  sampling values from the first 5 rows. */
function isNumericColumn(fieldName: string, rows: Record<string, any>[]): boolean {
  if (IDENTIFIER_NAME_RE.test(fieldName)) return false;
  for (const row of rows.slice(0, 5)) {
    const v = row[fieldName];
    if (v === null || v === undefined) continue;
    if (typeof v === "number") return true;
    if (typeof v === "string") {
      const trimmed = v.trim();
      if (!trimmed) continue;
      if (/^-?\$?[\d,]+(\.\d+)?%?$/.test(trimmed)) return true;
      return false;
    }
    return false;
  }
  return false;
}

/** Variance / change / delta / gain / loss columns get the red/green treatment. */
function isVarianceColumn(fieldName: string): boolean {
  return /variance|change|delta|gain|loss|diff(?:erence)?/i.test(fieldName);
}

/** Format a SQL column name into a readable header. Handles
 *  PascalCase ("YTDActuals" → "YTD Actuals"), camelCase, and snake_case. */
function formatHeader(name: string): string {
  let clean = name.replace(/^\[|\]$/g, "").trim();
  if (/\s/.test(clean)) return clean;
  clean = clean.replace(/([A-Z])([A-Z][a-z])/g, "$1 $2");
  clean = clean.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  clean = clean.replace(/_/g, " ");
  return clean.replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Detect whether a numeric value is negative for variance colouring. */
function isNegativeNumeric(raw: any): boolean {
  if (raw === null || raw === undefined) return false;
  if (typeof raw === "number") return raw < 0;
  if (typeof raw !== "string") return false;
  return raw.trim().startsWith("-");
}

function isPositiveNumeric(raw: any): boolean {
  if (raw === null || raw === undefined) return false;
  if (typeof raw === "number") return raw > 0;
  if (typeof raw !== "string") return false;
  const trimmed = raw.trim();
  if (trimmed.startsWith("-")) return false;
  return /^\$?[\d,]+(\.\d+)?%?$/.test(trimmed);
}

/** Format a cell value. Keeps the precise values (no abbreviation) — KPI
 *  cards abbreviate; tables show detail. Server-formatted strings
 *  ("$78,799,456.27", "-89.8%") are preserved as-is. Raw numbers get
 *  comma separators applied. */
function formatCell(raw: any, _fieldName: string): string {
  if (raw === null || raw === undefined) return "—";
  if (typeof raw === "string") return raw;
  if (typeof raw === "number") {
    if (Number.isInteger(raw)) return raw.toLocaleString("en-AU");
    return raw.toLocaleString("en-AU", { maximumFractionDigits: 2 });
  }
  return String(raw);
}

export default function DataTable({ data, caption, onDrilldown }: Props) {
  const { rows, fields, rowCount } = data;

  const numericByField = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const f of fields) map[f.name] = isNumericColumn(f.name, rows);
    return map;
  }, [fields, rows]);

  // Primary dimension = first column that is neither numeric nor identifier-
  // shaped by name. That's the one we make drillable (matches the chart's
  // x-axis intent). Falls back to undefined → no drill affordance.
  const drillField = useMemo(
    () =>
      fields.find(
        (f) => !numericByField[f.name] && !IDENTIFIER_NAME_RE.test(f.name)
      )?.name,
    [fields, numericByField]
  );
  const humanDrillField = drillField ? formatHeader(drillField) : "";
  const drillEnabled = !!onDrilldown && !!drillField;

  const varianceByField = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const f of fields) map[f.name] = isVarianceColumn(f.name);
    return map;
  }, [fields]);

  if (!rows || rows.length === 0) {
    return (
      <div className={styles.wrap}>
        {caption && (
          <div className={styles.head}>
            <span className={styles.caption}>{caption}</span>
          </div>
        )}
        <div className={styles.emptyState}>No rows to display.</div>
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.head}>
        {caption && <span className={styles.caption}>{caption}</span>}
        <span className={styles.rowCount}>
          {rowCount.toLocaleString("en-AU")} {rowCount === 1 ? "row" : "rows"}
        </span>
        {drillEnabled && (
          <span className={styles.drillHint}>
            <span className={styles.drillHintDot} />
            click {humanDrillField.toLowerCase()} to drill in
          </span>
        )}
      </div>
      <div className={styles.scroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              {fields.map((f) => (
                <th
                  key={f.name}
                  className={`${styles.th} ${numericByField[f.name] ? styles.thNum : ""}`}
                >
                  {formatHeader(f.name)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const isEven = idx % 2 === 1;
              const isLast = idx === rows.length - 1;
              return (
                <tr
                  key={idx}
                  className={`${isEven ? styles.rowEven : ""} ${styles.rowHover} ${isLast ? styles.rowLast : ""}`}
                >
                  {fields.map((f) => {
                    const value = row[f.name];
                    const isNumeric = numericByField[f.name];
                    const isVariance = varianceByField[f.name];
                    let deltaClass = "";
                    if (isNumeric && isVariance) {
                      if (isNegativeNumeric(value)) deltaClass = styles.deltaDown;
                      else if (isPositiveNumeric(value)) deltaClass = styles.deltaUp;
                    }
                    const isDrill =
                      drillEnabled &&
                      f.name === drillField &&
                      value !== null &&
                      value !== undefined &&
                      String(value).trim() !== "";
                    if (isDrill) {
                      const raw = String(value);
                      return (
                        <td
                          key={f.name}
                          className={`${styles.td} ${styles.drillCell}`}
                          role="button"
                          tabIndex={0}
                          title={`Drill into ${raw}`}
                          onClick={() => onDrilldown!(humanDrillField, raw)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onDrilldown!(humanDrillField, raw);
                            }
                          }}
                        >
                          <span className={`${styles.drillLabel} dt-drill-label`}>
                            {formatCell(value, f.name)}
                          </span>
                          <span
                            className={`${styles.drillArrow} dt-drill-arrow`}
                            aria-hidden="true"
                          >
                            ↳
                          </span>
                        </td>
                      );
                    }
                    return (
                      <td
                        key={f.name}
                        className={`${styles.td} ${isNumeric ? styles.tdNum : ""} ${deltaClass}`}
                      >
                        {formatCell(value, f.name)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
